import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { ArrowsClockwise, StopCircle, WhatsappLogo, ChatCircle, PencilSimple, FloppyDisk, X } from '@phosphor-icons/react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

const intervalLabel = (interval, months) => {
  const map = { quarterly: 'Quarterly', half_yearly: 'Half-Yearly', yearly: 'Yearly' };
  if (interval === 'custom' && months) return `${months}-Monthly`;
  return map[interval] || interval;
};

/**
 * Bucket a recurring row by its next renewal date. Buckets are evaluated
 * top-down — the first match wins, so order matters.
 */
function bucketOf(daysUntilDue) {
  if (daysUntilDue === null || daysUntilDue === undefined) return 'no_date';
  if (daysUntilDue < 0) return 'overdue';
  if (daysUntilDue === 0) return 'today';
  if (daysUntilDue <= 7) return 'this_week';
  if (daysUntilDue <= 30) return 'this_month';
  return 'later';
}

const BUCKETS = [
  { id: 'overdue',    label: 'Overdue',     color: 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' },
  { id: 'today',      label: 'Due Today',   color: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  { id: 'this_week',  label: 'This Week',   color: 'bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]' },
  { id: 'this_month', label: 'This Month',  color: 'bg-[#DBEAFE] text-[#1D4ED8] border-[#BFDBFE]' },
  { id: 'later',      label: 'Later',       color: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]' },
  { id: 'no_date',    label: 'Missing Date', color: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]' },
];

export default function RecurringPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stopTarget, setStopTarget] = useState(null);
  // Which row is currently in "edit remarks" mode. Keyed by
  // `${invoice_id}:${item_idx}` so different rows don't share state.
  const [editingKey, setEditingKey] = useState('');
  const [editingText, setEditingText] = useState('');
  const [savingKey, setSavingKey] = useState('');

  const rowKey = (r) => `${r.invoice_id}:${r.item_idx}`;

  const startEdit = (r) => {
    setEditingKey(rowKey(r));
    setEditingText(r.recurring_notes || '');
  };

  const cancelEdit = () => {
    setEditingKey('');
    setEditingText('');
  };

  const saveNotes = async (r) => {
    const key = rowKey(r);
    setSavingKey(key);
    try {
      await api.put(`/recurring/${r.invoice_id}/items/${r.item_idx}/notes`, {
        notes: editingText,
      });
      toast.success('Remarks updated');
      // Optimistically patch local state so we don't need to re-fetch the whole list.
      setRows(prev => prev.map(x => rowKey(x) === key ? { ...x, recurring_notes: editingText.trim() } : x));
      setEditingKey('');
      setEditingText('');
    } catch {
      toast.error('Failed to update remarks');
    } finally {
      setSavingKey('');
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/recurring');
      setRows(data || []);
    } catch {
      toast.error('Failed to load recurring subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRenew = (row) => {
    // Renew = open a new invoice pre-filled with the same customer + service.
    // The new subscription should START where the previous one ended — pass
    // `next_renewal_date` as `start_date` so the Recurring Period popup
    // defaults to the continuation date instead of today.
    const qs = new URLSearchParams({
      customer: row.customer_id,
      service: row.service_id,
    });
    if (row.next_renewal_date) qs.set('start_date', row.next_renewal_date);
    navigate(`/invoices/create?${qs.toString()}`);
  };

  const handleWhatsApp = (row) => {
    const phone = (row.customer_phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('No phone number on file'); return; }
    const msg = encodeURIComponent(
      `Hi ${row.customer_name}, your ${row.service_name} (${intervalLabel(row.recurring_interval, row.recurring_interval_months)}) is due for renewal on ${row.next_renewal_date}. Please visit us to renew.`
    );
    window.open(`https://wa.me/91${phone}?text=${msg}`, '_blank', 'noopener');
  };

  const handleSMS = (row) => {
    const phone = (row.customer_phone || '').replace(/\D/g, '');
    if (!phone) { toast.error('No phone number on file'); return; }
    const msg = encodeURIComponent(
      `Hi ${row.customer_name}, your ${row.service_name} is due for renewal on ${row.next_renewal_date}.`
    );
    window.open(`sms:${phone}?body=${msg}`, '_blank');
  };

  const confirmStop = async () => {
    if (!stopTarget) return;
    try {
      await api.post(`/recurring/${stopTarget.invoice_id}/items/${stopTarget.item_idx}/stop`);
      toast.success('Recurring stopped');
      setStopTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to stop');
    }
  };

  // Group rows into buckets while preserving the API's overall sort (by days_until_due asc).
  const grouped = BUCKETS.map(b => ({
    ...b,
    items: rows.filter(r => bucketOf(r.days_until_due) === b.id),
  })).filter(b => b.items.length > 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="recurring-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Recurring Subscriptions</h1>
        <p className="text-sm text-[#52525B]">Renewals tracked from your invoices — grouped by urgency.</p>
      </div>

      {loading ? (
        <p className="text-center text-sm text-[#A1A1AA] py-12" data-testid="recurring-loading">Loading…</p>
      ) : rows.length === 0 ? (
        <Card className="rounded-xl border-[#E4E4E7]">
          <CardContent className="py-12 text-center text-sm text-[#A1A1AA]" data-testid="recurring-empty">
            No recurring subscriptions yet. Mark a service as <em>Recurring</em> in Services to start tracking renewals here.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(bucket => (
            <Card key={bucket.id} className="rounded-xl border-[#E4E4E7] overflow-hidden">
              <CardContent className="p-0">
                <div className={`flex items-center justify-between px-4 py-2 border-b ${bucket.color}`}>
                  <p className="text-xs uppercase tracking-[0.12em] font-bold">{bucket.label}</p>
                  <Badge variant="outline" className="bg-white text-[#0A0A0A] rounded-md text-[10px]" data-testid={`recurring-bucket-count-${bucket.id}`}>
                    {bucket.items.length}
                  </Badge>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#E4E4E7]">
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Customer</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Service</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Interval</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Start</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Next Renewal</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold">Last Invoice</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold min-w-[180px]">Remarks</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-[0.12em] font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bucket.items.map((r, i) => (
                      <TableRow key={`${r.invoice_id}-${r.item_idx}`} className="border-[#E4E4E7]" data-testid={`recurring-row-${r.invoice_number}-${r.item_idx}`}>
                        <TableCell>
                          <p className="text-sm font-semibold text-[#0A0A0A]">{r.customer_name}</p>
                          <p className="text-[10px] text-[#A1A1AA] font-mono">{r.customer_phone || '—'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{r.service_name}</p>
                          <p className="text-[10px] text-[#A1A1AA] font-mono">{fmt(r.service_charge + (r.govt_fee || 0))}</p>
                        </TableCell>
                        <TableCell className="text-xs">{intervalLabel(r.recurring_interval, r.recurring_interval_months)}</TableCell>
                        <TableCell className="text-xs font-mono text-[#52525B]">{r.recurring_start_date ? new Date(r.recurring_start_date).toLocaleDateString('en-IN') : '—'}</TableCell>
                        <TableCell>
                          {r.next_renewal_date ? (
                            <>
                              <p className="text-sm font-semibold font-mono text-[#0A0A0A]">{new Date(r.next_renewal_date).toLocaleDateString('en-IN')}</p>
                              <p className={`text-[10px] font-mono ${r.days_until_due < 0 ? 'text-[#EF4444]' : r.days_until_due <= 7 ? 'text-[#F59E0B]' : 'text-[#A1A1AA]'}`}>
                                {r.days_until_due < 0 ? `${Math.abs(r.days_until_due)} day(s) overdue` : r.days_until_due === 0 ? 'Due today' : `in ${r.days_until_due} day(s)`}
                              </p>
                            </>
                          ) : <span className="text-[10px] text-[#A1A1AA]">—</span>}
                        </TableCell>
                        <TableCell className="text-xs font-mono text-[#3B82F6]">{r.invoice_number}</TableCell>
                        <TableCell className="text-xs" data-testid={`recurring-remarks-cell-${r.invoice_number}-${r.item_idx}`}>
                          {editingKey === rowKey(r) ? (
                            <div className="flex items-start gap-1">
                              <Textarea
                                value={editingText}
                                onChange={e => setEditingText(e.target.value)}
                                rows={2}
                                className="rounded-md text-xs min-w-[180px]"
                                placeholder="Policy #, vehicle #, portal ID…"
                                autoFocus
                                data-testid={`recurring-remarks-edit-${r.invoice_number}-${r.item_idx}`}
                              />
                              <div className="flex flex-col gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-[#16A34A]"
                                  title="Save"
                                  disabled={savingKey === rowKey(r)}
                                  onClick={() => saveNotes(r)}
                                  data-testid={`recurring-remarks-save-${r.invoice_number}-${r.item_idx}`}
                                >
                                  <FloppyDisk size={14} weight="fill" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-6 w-6 p-0 text-[#A1A1AA]"
                                  title="Cancel"
                                  onClick={cancelEdit}
                                  data-testid={`recurring-remarks-cancel-${r.invoice_number}-${r.item_idx}`}
                                >
                                  <X size={14} />
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-start gap-1 group max-w-[240px]">
                              <p
                                className={`whitespace-pre-wrap break-words ${r.recurring_notes ? 'text-[#0A0A0A]' : 'italic text-[#A1A1AA]'}`}
                                title={r.recurring_notes || 'No remarks yet'}
                                data-testid={`recurring-remarks-text-${r.invoice_number}-${r.item_idx}`}
                              >
                                {r.recurring_notes || '—'}
                              </p>
                              <button
                                type="button"
                                onClick={() => startEdit(r)}
                                className="opacity-40 group-hover:opacity-100 transition-opacity text-[#3B82F6] hover:text-[#2563EB] mt-0.5"
                                title="Edit remarks"
                                data-testid={`recurring-remarks-edit-btn-${r.invoice_number}-${r.item_idx}`}
                              >
                                <PencilSimple size={12} />
                              </button>
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[#16A34A]" onClick={() => handleWhatsApp(r)} title="WhatsApp reminder" data-testid={`recurring-wa-${i}`}>
                              <WhatsappLogo size={14} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-[#3B82F6]" onClick={() => handleSMS(r)} title="SMS reminder" data-testid={`recurring-sms-${i}`}>
                              <ChatCircle size={14} />
                            </Button>
                            <Button size="sm" className="h-7 px-3 text-xs bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-lg" onClick={() => handleRenew(r)} data-testid={`recurring-renew-${i}`}>
                              <ArrowsClockwise size={12} className="mr-1" /> Renew
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-[#EF4444] border-[#FCA5A5] hover:bg-[#FEF2F2]" onClick={() => setStopTarget(r)} title="Stop recurring" data-testid={`recurring-stop-${i}`}>
                              <StopCircle size={12} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <AlertDialog open={!!stopTarget} onOpenChange={(v) => { if (!v) setStopTarget(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Stop this recurring subscription?</AlertDialogTitle>
            <AlertDialogDescription>
              {stopTarget && (
                <>
                  <strong>{stopTarget.customer_name}</strong> · {stopTarget.service_name} ({intervalLabel(stopTarget.recurring_interval, stopTarget.recurring_interval_months)}).
                  <br />
                  No more renewal reminders will surface for this customer + service. You can still raise the invoice manually.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="recurring-stop-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmStop} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="recurring-stop-confirm">
              Stop
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
